import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

import { UserSelectionGrid } from '@/components/ui/UserSelectionGrid';
import { UserGridView } from '@/components/ui/UserGridView';
import { CarouselSettings } from '@/components/ui/CarouselSettings';
import { HighlightsCarousel } from '@/components/ui/HighlightsCarousel';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/contexts/ToastContext';
import { Save, Eye, EyeOff, Settings } from 'lucide-react';

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  profileImageUrl?: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  role: 'aspirant' | 'mentor';
}

interface CarouselSettings {
  speed: number;
  direction: 'left' | 'right';
  focusCardSize: number;
  normalCardSize: number;
}

interface NavigationSettings {
  showInMainNav: boolean;
}

interface HighlightsData {
  users: User[];
  settings: CarouselSettings;
  navigationSettings: NavigationSettings;
}

const defaultSettings: CarouselSettings = {
  speed: 50,
  direction: 'right',
  focusCardSize: 120,
  normalCardSize: 80
};

const defaultNavigationSettings: NavigationSettings = {
  showInMainNav: false
};

export default function HighlightsManagement() {
  const { success, error } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [userViewMode, setUserViewMode] = useState<'grid' | 'list'>('grid');
  
  // Data states
  const [highlightsData, setHighlightsData] = useState<HighlightsData>({
    users: [],
    settings: defaultSettings,
    navigationSettings: defaultNavigationSettings
  });
  
  // User selection states
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100, // Increased limit to show more users per page
    total: 0,
    pages: 0
  });
  const [usersLoading, setUsersLoading] = useState(false);

  // Load existing highlights data
  useEffect(() => {
    loadHighlightsData();
  }, []);

  // Load users for selection
  useEffect(() => {
    loadUsers();
  }, [pagination.page]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ role: 'all', hasProfilePic: 'all' });

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadUsers({
        search: searchTerm,
        role: filters.role,
        hasProfilePic: filters.hasProfilePic
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filters]);

  // Handle search change
  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  // Handle filter change
  const handleFilterChange = (newFilters: { role: string; hasProfilePic: string }) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const loadHighlightsData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/highlights/admin', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Highlights data received:', JSON.stringify(data, null, 2));
        
        if (data.success) {
          console.log('Raw data from backend:', data.data);
          
          // Keep aspirants and mentors separate
          let aspirantsUsers = [];
          let mentorsUsers = [];
          let combinedSettings = defaultSettings;
          let combinedNavigationSettings = defaultNavigationSettings;
          
          if (data.data.aspirants) {
            // Handle Mongoose populated data structure
            const aspirantsRawUsers = data.data.aspirants.users || [];
            console.log('Aspirants users raw:', aspirantsRawUsers);
            
            // Extract user data from the populated structure
            aspirantsUsers = aspirantsRawUsers.map((userEntry: any) => {
              // The user data might be in userEntry.userId (populated) or userEntry itself
              const userData = userEntry.userId || userEntry;
              console.log('Processing aspirant user entry:', userEntry, 'User data:', userData);
              return userData;
            }).filter(Boolean);
            
            console.log('Processed aspirants users:', aspirantsUsers);
            
            if (data.data.aspirants.settings) {
              combinedSettings = { ...combinedSettings, ...data.data.aspirants.settings };
            }
            if (data.data.aspirants.navigationSettings) {
              combinedNavigationSettings = { ...combinedNavigationSettings, ...data.data.aspirants.navigationSettings };
            }
          }
          
          if (data.data.mentors) {
            // Handle Mongoose populated data structure
            const mentorsRawUsers = data.data.mentors.users || [];
            console.log('Mentors users raw:', mentorsRawUsers);
            
            // Extract user data from the populated structure
            mentorsUsers = mentorsRawUsers.map((userEntry: any) => {
              // The user data might be in userEntry.userId (populated) or userEntry itself
              const userData = userEntry.userId || userEntry;
              console.log('Processing mentor user entry:', userEntry, 'User data:', userData);
              return userData;
            }).filter(Boolean);
            
            console.log('Processed mentors users:', mentorsUsers);
            
            if (data.data.mentors.settings) {
              combinedSettings = { ...combinedSettings, ...data.data.mentors.settings };
            }
            if (data.data.mentors.navigationSettings) {
              combinedNavigationSettings = { ...combinedNavigationSettings, ...data.data.mentors.navigationSettings };
            }
          }
          
          // Combine all users for the selection interface, but keep track of their roles
          const allUsers = [...aspirantsUsers, ...mentorsUsers];
          
          console.log('Final data:', {
            aspirantsUsers,
            mentorsUsers,
            allUsers,
            settings: combinedSettings,
            navigationSettings: combinedNavigationSettings
          });
          
          setHighlightsData({
            users: allUsers,
            settings: combinedSettings,
            navigationSettings: combinedNavigationSettings
          });
          setSelectedUsers(allUsers);
        }
      }
    } catch (err) {
      console.error('Error loading highlights data:', err);
      error('Error', 'Failed to load highlights data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (searchParams = {}) => {
    try {
      setUsersLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...searchParams
      });
      
      const response = await fetch(`/api/highlights/admin/users?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          setAvailableUsers(data.data.users);
          setPagination(data.data.pagination);
        }
      }
    } catch (err) {
      console.error('Error loading users:', err);
      error('Error', 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserToggle = (user: User) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u._id === user._id);
      if (isSelected) {
        return prev.filter(u => u._id !== user._id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedUsers(availableUsers);
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
  };

  // Debounced auto-save function
              const debouncedSaveSettings = React.useCallback(
              debounce(async (settings: CarouselSettings, navigationSettings: { showInMainNav: boolean }) => {
                try {
                  setSettingsSaving(true);
                  await saveSettings(settings, navigationSettings);
                } catch (error) {
                  console.error('Error auto-saving settings:', error);
                } finally {
                  setSettingsSaving(false);
                }
              }, 500), // Reduced to 500ms for faster auto-save
              []
            );

  const handleSettingsChange = async (settings: Partial<CarouselSettings>) => {
    const newSettings = { ...highlightsData.settings, ...settings };
    setHighlightsData(prev => ({
      ...prev,
      settings: newSettings
    }));
    
    // Debounced auto-save settings
    debouncedSaveSettings(newSettings, highlightsData.navigationSettings);
  };

  const handleNavigationSettingsChange = async (showInMainNav: boolean) => {
    const newNavigationSettings = { showInMainNav };
    setHighlightsData(prev => ({
      ...prev,
      navigationSettings: newNavigationSettings
    }));
    
    // Debounced auto-save navigation settings
    debouncedSaveSettings(highlightsData.settings, newNavigationSettings);
  };

  const saveSettings = async (settings: CarouselSettings, navigationSettings: { showInMainNav: boolean }) => {
    try {
      // For auto-saving settings, we should preserve existing users in the database
      // Only update settings and navigation settings, not user selection
      const payload = {
        aspirants: {
          settings,
          navigationSettings
        },
        mentors: {
          settings,
          navigationSettings
        }
      };

      const response = await fetch('/api/highlights/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        console.error('Failed to auto-save settings');
      } else {
        console.log('Settings auto-saved successfully');
      }
    } catch (error) {
      console.error('Error auto-saving settings:', error);
    }
  };

  const handleSaveUserSelection = async () => {
    try {
      setSaving(true);
      
      // Separate users by role
      const aspirantsUsers = selectedUsers.filter(user => user && user._id && user.role === 'aspirant').map(user => ({
        userId: user._id,
        profilePic: user.profileImageUrl || null,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'
      }));

      const mentorsUsers = selectedUsers.filter(user => user && user._id && user.role === 'mentor').map(user => ({
        userId: user._id,
        profilePic: user.profileImageUrl || null,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'
      }));

      const payload = {
        aspirants: {
          users: aspirantsUsers,
          settings: highlightsData.settings,
          navigationSettings: highlightsData.navigationSettings
        },
        mentors: {
          users: mentorsUsers,
          settings: highlightsData.settings,
          navigationSettings: highlightsData.navigationSettings
        }
      };
      
      console.log('Highlights data:', highlightsData);
      console.log('Selected users:', selectedUsers);
      console.log('Saving user selection payload:', JSON.stringify(payload, null, 2));

      const response = await fetch('/api/highlights/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          // Update the highlightsData with the new user selection without reloading
          setHighlightsData(prev => ({
            ...prev,
            users: selectedUsers
          }));
          success('Success', 'User selection saved successfully');
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Backend error response:', errorData);
        throw new Error(`Failed to save user selection: ${errorData.message || response.statusText}`);
      }
    } catch (err) {
      console.error('Error saving user selection:', err);
      error('Error', 'Failed to save user selection');
    } finally {
      setSaving(false);
    }
  };

  const currentData = highlightsData;

  return (
    <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-secondary">Highlights Management</h1>
          <p className="text-sm text-gray-600">
            Select users and configure carousel settings for the highlights page
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Selection */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Select Users</h3>
                <Button
                  onClick={handleSaveUserSelection}
                  disabled={saving}
                  className="bg-primary hover:bg-primary-dark"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save User Selection'}
                </Button>
              </div>
              <UserSelectionGrid
                users={availableUsers}
                selectedUsers={selectedUsers}
                onUserToggle={handleUserToggle}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                loading={usersLoading}
                pagination={pagination}
                onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                onSearchChange={handleSearchChange}
                onFilterChange={handleFilterChange}
              />
            </Card>
          </div>

          {/* Settings */}
          <div className="space-y-6">
            <div className="relative">
              <CarouselSettings
                settings={currentData.settings}
                onSettingsChange={handleSettingsChange}
                title="Carousel Settings"
              />
              {settingsSaving && (
                <div className="absolute top-4 right-4 flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                  Auto-saving...
                </div>
              )}
            </div>
            
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Navigation Settings</h3>
                {settingsSaving && (
                  <div className="flex items-center text-xs text-blue-600">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                    Auto-saving...
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Show in Main Navigation</Label>
                <Switch
                  checked={currentData.navigationSettings?.showInMainNav || false}
                  onCheckedChange={handleNavigationSettingsChange}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                When enabled, "Highlights" will appear in the main navigation bar
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Live Preview */}
      {showPreview && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h3>
          <div className="space-y-8">
                        <HighlightsCarousel
              users={selectedUsers.filter(user => user).map(user => ({
                userId: user._id || '',
                _id: user._id,
                profilePic: user?.profileImageUrl || undefined,
                name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown User',
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                role: user.role,
                address: user.address,
                primarySkills: user.primarySkills,
                secondarySkills: user.secondarySkills
              }))}
              settings={highlightsData.settings}
              title="Highlights"
            />
          </div>
        </Card>
      )}
    </div>
  );
}
